import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../BottomSheet";
import { Button } from "../Button";
import {
  type MobileDateRange,
  MobileDateRangePicker,
} from "./MobileDateRangePicker";

type MobileDateRangeBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onApply: (range: { from: Date; to: Date }) => void;
  /** Pre-existing custom range, if the user has applied one before */
  initialRange: { from: Date; to: Date } | null;
  /** Range Reset restores Start/End to (e.g. "Last 30 days") -- falls back
   * to blank when omitted. */
  defaultRange?: { from: Date; to: Date } | null;
  /** Earliest selectable date. Defaults to 1 Jan 2021. */
  minDate?: Date;
  /** Latest selectable date. Defaults to today. */
  maxDate?: Date;
  limitDays?: number;
  /** When true, the sheet shows a drag handle and hides the close (×) icon —
   * used when it's a pushed step inside a larger flow (e.g. the date-range
   * selector's "Custom date range" step). Defaults to false to preserve the
   * existing standalone behaviour on the client report pages. */
  isDraggable?: boolean;
};

const DEFAULT_MIN_DATE = new Date(2021, 0, 1);

export const MobileDateRangeBottomSheet = ({
  isOpen,
  onClose,
  onBack,
  onApply,
  initialRange,
  defaultRange,
  minDate = DEFAULT_MIN_DATE,
  maxDate = new Date(),
  limitDays,
  isDraggable = false,
}: MobileDateRangeBottomSheetProps) => {
  const { t } = useTranslation();

  const translated = useMemo(
    () => ({
      customDateRange: t("clientsPage_filters_Bottomsheet_customDateRange"),
      confirm: t("clientsPage_filters_Bottomsheet_confirm"),
      reset: t("clientsPage_filters_Bottomsheet_reset"),
      apply: t("clientsPage_filters_Bottomsheet_apply"),
    }),
    [t],
  );

  // Open with the previously applied range, or no selection if this is the first time
  const [range, setRange] = useState<MobileDateRange>(
    initialRange ?? { from: null, to: null },
  );

  // Hide the Reset/Apply footer while the wheel picker is open
  const [isPickerMode, setIsPickerMode] = useState(false);

  // When the sheet opens, restore the applied range (or clear if none) and
  // make sure the picker is closed.
  const [syncedWith, setSyncedWith] = useState({ isOpen, initialRange });
  const [openCount, setOpenCount] = useState(0);
  if (
    syncedWith.isOpen !== isOpen ||
    syncedWith.initialRange !== initialRange
  ) {
    setSyncedWith({ isOpen, initialRange });
    if (isOpen) {
      setRange(initialRange ?? { from: null, to: null });
      setIsPickerMode(false);
      if (!syncedWith.isOpen) setOpenCount((count) => count + 1);
    }
  }

  const handleReset = () => {
    // Restore Start/End to the default range (e.g. "Last 30 days") rather
    // than leaving them blank, matching the desktop Filter modal's Clear.
    // This only touches the local draft -- like Clear on desktop, it isn't
    // committed until Apply is also clicked; closing (Back/X) without
    // applying discards it, leaving the real applied range untouched.
    setRange(defaultRange ?? { from: null, to: null });
  };

  const handleApply = () => {
    if (range.from && range.to) {
      onApply({ from: range.from, to: range.to });
    } else if (range.from) {
      // If user only picked a start, treat it as a single-day range
      onApply({ from: range.from, to: range.from });
    }
  };

  const canApply = Boolean(range.from);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      onBack={onBack}
      backLabel={translated.customDateRange}
      isDraggable={isDraggable}
      title=""
      footer={
        isPickerMode ? undefined : (
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="md"
              className="flex-1"
              onClick={handleReset}
            >
              {translated.reset}
            </Button>
            <Button
              variant="brand"
              size="md"
              className="flex-1"
              onClick={handleApply}
              disabled={!canApply}
            >
              {translated.apply}
            </Button>
          </div>
        )
      }
    >
      <MobileDateRangePicker
        key={`${openCount}-${initialRange?.from?.getTime() ?? "none"}`}
        value={range}
        onChange={setRange}
        minDate={minDate}
        maxDate={maxDate}
        limitDays={limitDays}
        initialViewDate={initialRange?.from}
        onViewChange={(v) => setIsPickerMode(v === "picker")}
        confirmLabel={translated.confirm}
      />
    </BottomSheet>
  );
};
